/**
 * Settings Context Provider
 * Provides reactive settings management across all admin components
 * Updates automatically when localStorage changes
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { STORAGE_KEYS } from '../constants';

const SettingsContext = createContext(null);

/**
 * Settings Provider Component
 * Wraps admin app to provide reactive settings
 */
export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(() => {
    // Initialize settings from localStorage
    const getAdminEmailKey = () => {
      if (typeof window === 'undefined') return 'default';
      const adminEmail = localStorage.getItem(STORAGE_KEYS.ADMIN_EMAIL) || 'default';
      return adminEmail.replace(/@/g, '_').replace(/\./g, '_');
    };

    const loadPreference = (key, defaultValue) => {
      if (typeof window === 'undefined') return defaultValue;
      const stored = localStorage.getItem(key);
      if (stored === null) return defaultValue;
      if (typeof defaultValue === 'boolean') return stored === 'true';
      if (typeof defaultValue === 'number') {
        const num = parseInt(stored);
        return isNaN(num) ? defaultValue : num;
      }
      return stored;
    };

    return {
      // General preferences
      autoRefresh: loadPreference(STORAGE_KEYS.AUTO_REFRESH, true),
      autoRefreshInterval: loadPreference(STORAGE_KEYS.AUTO_REFRESH_INTERVAL, 30),
      compactMode: loadPreference(STORAGE_KEYS.COMPACT_MODE, false),
      sidebarCollapsed: loadPreference(STORAGE_KEYS.SIDEBAR_COLLAPSED, false),
      
      // Table preferences
      tableItemsPerPage: loadPreference(STORAGE_KEYS.TABLE_ITEMS_PER_PAGE, 10),
      
      // Display preferences
      dateFormat: loadPreference(STORAGE_KEYS.DATE_FORMAT, 'MM/DD/YYYY'),
      currencyFormat: loadPreference(STORAGE_KEYS.CURRENCY_FORMAT, 'USD'),
      numberFormat: loadPreference(STORAGE_KEYS.NUMBER_FORMAT, 'standard'),
      
      // UI preferences
      showTooltips: loadPreference(STORAGE_KEYS.SHOW_TOOLTIPS, true),
      chartAnimations: loadPreference(STORAGE_KEYS.CHART_ANIMATIONS, true),
      
      // Dashboard preferences
      dashboardWidgets: loadPreference(STORAGE_KEYS.DASHBOARD_WIDGETS, 'all'),
    };
  });

  // Update settings when localStorage changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key && Object.values(STORAGE_KEYS).includes(e.key)) {
        // Reload settings from localStorage
        const getAdminEmailKey = () => {
          const adminEmail = localStorage.getItem(STORAGE_KEYS.ADMIN_EMAIL) || 'default';
          return adminEmail.replace(/@/g, '_').replace(/\./g, '_');
        };

        const loadPreference = (key, defaultValue) => {
          const stored = localStorage.getItem(key);
          if (stored === null) return defaultValue;
          if (typeof defaultValue === 'boolean') return stored === 'true';
          if (typeof defaultValue === 'number') {
            const num = parseInt(stored);
            return isNaN(num) ? defaultValue : num;
          }
          return stored;
        };

        setSettings({
          autoRefresh: loadPreference(STORAGE_KEYS.AUTO_REFRESH, true),
          autoRefreshInterval: loadPreference(STORAGE_KEYS.AUTO_REFRESH_INTERVAL, 30),
          compactMode: loadPreference(STORAGE_KEYS.COMPACT_MODE, false),
          sidebarCollapsed: loadPreference(STORAGE_KEYS.SIDEBAR_COLLAPSED, false),
          tableItemsPerPage: loadPreference(STORAGE_KEYS.TABLE_ITEMS_PER_PAGE, 10),
          dateFormat: loadPreference(STORAGE_KEYS.DATE_FORMAT, 'MM/DD/YYYY'),
          currencyFormat: loadPreference(STORAGE_KEYS.CURRENCY_FORMAT, 'USD'),
          numberFormat: loadPreference(STORAGE_KEYS.NUMBER_FORMAT, 'standard'),
          showTooltips: loadPreference(STORAGE_KEYS.SHOW_TOOLTIPS, true),
          chartAnimations: loadPreference(STORAGE_KEYS.CHART_ANIMATIONS, true),
          dashboardWidgets: loadPreference(STORAGE_KEYS.DASHBOARD_WIDGETS, 'all'),
        });
      }
    };

    // Listen for storage events (for cross-tab sync)
    window.addEventListener('storage', handleStorageChange);
    
    // Poll for localStorage changes (since storage event doesn't fire in same tab)
    const interval = setInterval(() => {
      const getAdminEmailKey = () => {
        const adminEmail = localStorage.getItem(STORAGE_KEYS.ADMIN_EMAIL) || 'default';
        return adminEmail.replace(/@/g, '_').replace(/\./g, '_');
      };

      const loadPreference = (key, defaultValue) => {
        const stored = localStorage.getItem(key);
        if (stored === null) return defaultValue;
        if (typeof defaultValue === 'boolean') return stored === 'true';
        if (typeof defaultValue === 'number') {
          const num = parseInt(stored);
          return isNaN(num) ? defaultValue : num;
        }
        return stored;
      };

      const newSettings = {
        autoRefresh: loadPreference(STORAGE_KEYS.AUTO_REFRESH, true),
        autoRefreshInterval: loadPreference(STORAGE_KEYS.AUTO_REFRESH_INTERVAL, 30),
        compactMode: loadPreference(STORAGE_KEYS.COMPACT_MODE, false),
        sidebarCollapsed: loadPreference(STORAGE_KEYS.SIDEBAR_COLLAPSED, false),
        tableItemsPerPage: loadPreference(STORAGE_KEYS.TABLE_ITEMS_PER_PAGE, 10),
        dateFormat: loadPreference(STORAGE_KEYS.DATE_FORMAT, 'MM/DD/YYYY'),
        currencyFormat: loadPreference(STORAGE_KEYS.CURRENCY_FORMAT, 'USD'),
        numberFormat: loadPreference(STORAGE_KEYS.NUMBER_FORMAT, 'standard'),
        showTooltips: loadPreference(STORAGE_KEYS.SHOW_TOOLTIPS, true),
        chartAnimations: loadPreference(STORAGE_KEYS.CHART_ANIMATIONS, true),
        dashboardWidgets: loadPreference(STORAGE_KEYS.DASHBOARD_WIDGETS, 'all'),
      };

      // Update if settings changed
      setSettings(prev => {
        const changed = Object.keys(newSettings).some(key => prev[key] !== newSettings[key]);
        return changed ? newSettings : prev;
      });
    }, 500); // Check every 500ms

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Update a specific setting
  const updateSetting = useCallback((key, value) => {
    localStorage.setItem(key, value.toString());
    setSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  const value = {
    ...settings,
    updateSetting,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

/**
 * Hook to access settings context
 * @returns {Object} - Settings object with updateSetting method
 */
export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    // Fallback to direct localStorage read if context not available
    const getAdminEmailKey = () => {
      if (typeof window === 'undefined') return 'default';
      const adminEmail = localStorage.getItem(STORAGE_KEYS.ADMIN_EMAIL) || 'default';
      return adminEmail.replace(/@/g, '_').replace(/\./g, '_');
    };

    const loadPreference = (key, defaultValue) => {
      if (typeof window === 'undefined') return defaultValue;
      const stored = localStorage.getItem(key);
      if (stored === null) return defaultValue;
      if (typeof defaultValue === 'boolean') return stored === 'true';
      if (typeof defaultValue === 'number') {
        const num = parseInt(stored);
        return isNaN(num) ? defaultValue : num;
      }
      return stored;
    };

    return {
      autoRefresh: loadPreference(STORAGE_KEYS.AUTO_REFRESH, true),
      autoRefreshInterval: loadPreference(STORAGE_KEYS.AUTO_REFRESH_INTERVAL, 30),
      compactMode: loadPreference(STORAGE_KEYS.COMPACT_MODE, false),
      sidebarCollapsed: loadPreference(STORAGE_KEYS.SIDEBAR_COLLAPSED, false),
      tableItemsPerPage: loadPreference(STORAGE_KEYS.TABLE_ITEMS_PER_PAGE, 10),
      dateFormat: loadPreference(STORAGE_KEYS.DATE_FORMAT, 'MM/DD/YYYY'),
      currencyFormat: loadPreference(STORAGE_KEYS.CURRENCY_FORMAT, 'USD'),
      numberFormat: loadPreference(STORAGE_KEYS.NUMBER_FORMAT, 'standard'),
      showTooltips: loadPreference(STORAGE_KEYS.SHOW_TOOLTIPS, true),
      chartAnimations: loadPreference(STORAGE_KEYS.CHART_ANIMATIONS, true),
      dashboardWidgets: loadPreference(STORAGE_KEYS.DASHBOARD_WIDGETS, 'all'),
      updateSetting: () => {},
    };
  }
  return context;
};

